package link.erpango.quip.features.noteimport

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import link.erpango.quip.domain.entities.DExtractionError
import link.erpango.quip.domain.entities.DExtractionStatus
import link.erpango.quip.domain.noteimport.ImportDraft
import link.erpango.quip.domain.noteimport.NoteImportEvent
import link.erpango.quip.domain.noteimport.NoteImportLimits
import link.erpango.quip.domain.usecases.INoteImportUseCase
import link.erpango.quip.domain.usecases.NoteImportAiResult

/** Why the AI option can not be used right now; the screen maps it to a string. */
enum class NoteImportAiMessage {
    RATE_LIMITED, DAILY_QUOTA, GLOBAL_QUOTA, UNAVAILABLE, TIMEOUT, FAILED, NETWORK, TOO_LONG, GENERIC
}

data class NoteAnalysisState(
    val note: String = "",
    /** The pasted text was longer than [NoteImportLimits.MAX_NOTE_LENGTH] and was cut. */
    val wasTruncated: Boolean = false,
    /** Null until the note has been analyzed. */
    val draft: ImportDraft? = null,
    /** The note [draft] was built from: source spans point into it. */
    val analyzedNote: String = "",
    val isAnalyzing: Boolean = false,
    val isAiLoading: Boolean = false,
    /** False until the backend confirms a provider is configured. */
    val aiAvailable: Boolean = false,
    val aiDailyLimit: Int = 0,
    val aiRemainingToday: Int? = null,
    /** Seconds until the AI can be used again after a per-minute limit. */
    val aiCooldownSeconds: Int = 0,
    /** Daily quota (the user's or the global one) used up: no more AI today. */
    val aiDailyExhausted: Boolean = false,
    val aiMessage: NoteImportAiMessage? = null,
    val showConsent: Boolean = false,
) {
    val canUseAi: Boolean
        get() = aiAvailable && !isAiLoading && !isAnalyzing && aiCooldownSeconds == 0 &&
            !aiDailyExhausted && note.isNotBlank()
}

/**
 * Paste box + offline analysis + optional AI of the "importar desde nota" assistant, shared by
 * the per-form dialog and the bulk import screen. Owns no UI: the owner passes its scope.
 *
 * The AI is only called after an explicit tap and with stored consent; its limits (per-minute
 * cooldown, daily quota) are reflected in the state so the button is disabled instead of
 * producing an error on every tap.
 */
class NoteAnalysisController(
    private val useCase: INoteImportUseCase,
    private val scope: CoroutineScope,
    /** Called every time a new draft is available (local analysis or AI merge). */
    private val onDraft: (ImportDraft) -> Unit = {},
) {
    private val _state = MutableStateFlow(NoteAnalysisState())
    val state: StateFlow<NoteAnalysisState> = _state.asStateFlow()

    private var aiJob: Job? = null
    private var cooldownJob: Job? = null

    /** Resets everything and checks whether the AI option is available. */
    fun start() {
        clear()
        scope.launch { refreshStatus() }
    }

    /** Forgets the note and the result, cancelling any AI call in flight. */
    fun clear() {
        aiJob?.cancel()
        cooldownJob?.cancel()
        _state.value = NoteAnalysisState()
    }

    fun onNoteChange(text: String) {
        val clamped = text.take(NoteImportLimits.MAX_NOTE_LENGTH)
        _state.update {
            if (it.note == clamped) it
            else it.copy(note = clamped, wasTruncated = text.length > NoteImportLimits.MAX_NOTE_LENGTH, aiMessage = null)
        }
    }

    fun analyze() {
        val note = _state.value.note
        if (note.isBlank() || _state.value.isAnalyzing) return
        _state.update { it.copy(isAnalyzing = true, aiMessage = null) }
        scope.launch {
            val draft = useCase.analyzeLocally(note)
            _state.update { it.copy(isAnalyzing = false, draft = draft, analyzedNote = note) }
            useCase.track(NoteImportEvent.LocalAnalyzed(draft))
            onDraft(draft)
        }
    }

    /** "Mejorar con IA": asks for consent the first time, then sends the note. */
    fun requestAi() {
        if (!_state.value.canUseAi) return
        scope.launch {
            if (useCase.hasAiConsent()) runAi() else _state.update { it.copy(showConsent = true) }
        }
    }

    fun onConsent(granted: Boolean) {
        _state.update { it.copy(showConsent = false) }
        scope.launch {
            useCase.setAiConsent(granted)
            useCase.track(NoteImportEvent.AiConsent(granted))
            if (granted) runAi()
        }
    }

    fun cancelAi() {
        aiJob?.cancel()
        aiJob = null
        _state.update { it.copy(isAiLoading = false) }
    }

    private fun runAi() {
        if (!_state.value.canUseAi) return
        val note = _state.value.note
        _state.update { it.copy(isAiLoading = true, aiMessage = null) }
        aiJob = scope.launch {
            val current = _state.value
            val local = current.draft?.takeIf { current.analyzedNote == note } ?: useCase.analyzeLocally(note)
            useCase.track(NoteImportEvent.AiRequested)
            val result = useCase.improveWithAi(note, local)
            when (result) {
                is NoteImportAiResult.Success -> {
                    _state.update { it.copy(isAiLoading = false, draft = result.draft, analyzedNote = note) }
                    useCase.track(NoteImportEvent.AiResult(null))
                    onDraft(result.draft)
                    refreshStatus()
                }

                is NoteImportAiResult.Failure -> {
                    // The local result stays: the AI failing never loses what the parser found.
                    _state.update { it.copy(isAiLoading = false, draft = local, analyzedNote = note) }
                    if (current.draft == null || current.analyzedNote != note) onDraft(local)
                    useCase.track(NoteImportEvent.AiResult(result.error))
                    handleAiError(result.error)
                }
            }
        }
    }

    private suspend fun handleAiError(error: DExtractionError) {
        when (error) {
            is DExtractionError.RateLimited -> startCooldown(error.retryAfterSeconds)
            is DExtractionError.DailyQuotaExceeded ->
                _state.update { it.copy(aiDailyExhausted = true, aiRemainingToday = 0, aiMessage = NoteImportAiMessage.DAILY_QUOTA) }
            is DExtractionError.GlobalQuotaExceeded ->
                _state.update { it.copy(aiDailyExhausted = true, aiMessage = NoteImportAiMessage.GLOBAL_QUOTA) }
            DExtractionError.Unavailable ->
                _state.update { it.copy(aiAvailable = false, aiMessage = NoteImportAiMessage.UNAVAILABLE) }
            DExtractionError.Timeout -> fail(NoteImportAiMessage.TIMEOUT)
            DExtractionError.ExtractionFailed -> {
                fail(NoteImportAiMessage.FAILED)
                refreshStatus() // a failed extraction still used quota
            }
            DExtractionError.Network -> fail(NoteImportAiMessage.NETWORK)
            DExtractionError.NoteTooLong -> fail(NoteImportAiMessage.TOO_LONG)
            DExtractionError.EmptyNote, DExtractionError.ProviderError,
            DExtractionError.Unauthorized, DExtractionError.Unknown -> fail(NoteImportAiMessage.GENERIC)
        }
    }

    private fun fail(message: NoteImportAiMessage) = _state.update { it.copy(aiMessage = message) }

    private suspend fun refreshStatus() {
        val status: DExtractionStatus = useCase.aiStatus() ?: return
        _state.update {
            it.copy(
                aiAvailable = status.enabled,
                aiDailyLimit = status.dailyLimit,
                aiRemainingToday = status.remainingToday,
                aiDailyExhausted = status.enabled && status.dailyLimit > 0 && status.remainingToday == 0,
            )
        }
        if (status.remainingToday > 0 && status.retryAfter > 0) startCooldown(status.retryAfter, showMessage = false)
    }

    private fun startCooldown(seconds: Int, showMessage: Boolean = true) {
        cooldownJob?.cancel()
        _state.update {
            it.copy(
                aiCooldownSeconds = seconds.coerceAtLeast(1),
                aiMessage = if (showMessage) NoteImportAiMessage.RATE_LIMITED else it.aiMessage,
            )
        }
        cooldownJob = scope.launch {
            while (_state.value.aiCooldownSeconds > 0) {
                delay(1_000)
                _state.update {
                    val left = it.aiCooldownSeconds - 1
                    it.copy(
                        aiCooldownSeconds = left.coerceAtLeast(0),
                        aiMessage = if (left <= 0 && it.aiMessage == NoteImportAiMessage.RATE_LIMITED) null else it.aiMessage,
                    )
                }
            }
        }
    }
}
